import type { EndpointContour, ConnectionEndpointSurface } from "./ConnectionEndpointProvider";

type Point = { clientX: number; clientY: number };
type Direction = { x: number; y: number };
type ContouredEndpoint = { point: Point; contour?: EndpointContour; exitDirection?: Direction; surface?: ConnectionEndpointSurface };

const CONNECTION_CONTOUR_GAP = 2;
const NAVIGATOR_CONNECTION_INSET = 24;

/** Insets the attachment past the scrollbar while retaining the row's vertically pinned position. */
export function navigatorEndpointGeometry(element: Element, point: Point, includeHoveredMenu = true): ContouredEndpoint {
  const navigator = element.closest(".navigationTreeOuter");
  if (!navigator) return { point };
  const bounds = navigator.getBoundingClientRect();
  const row = element.getBoundingClientRect();
  if (bounds.width <= 0 || row.height <= 0) return { point };
  const inset = Math.min(NAVIGATOR_CONNECTION_INSET + CONNECTION_CONTOUR_GAP, bounds.width / 2);
  let width = bounds.width - inset;
  // Give the flyout its entire pointer area, including the connection's 6px hit-stroke radius.
  const menu = includeHoveredMenu ? element.querySelector(".treeMenuButtons") : null;
  if (menu && (getComputedStyle(menu).visibility === "visible" || element.contains(document.activeElement))) {
    const menuBounds = menu.getBoundingClientRect();
    if (menuBounds.width > 0) width = Math.max(width, menuBounds.right - bounds.left + 6);
  }
  return {
    point: { clientX: bounds.left + width / 2, clientY: point.clientY },
    contour: { type: "rectangle", width, height: row.height },
  };
}

/** Measures real symbols, retaining SVG rotation and scale without attaching contours to pinned substitutes. */
export function measureEndpointContour(element: Element, point: Point, surface: ConnectionEndpointSurface): EndpointContour | undefined {
  if (surface !== "drawing" && surface !== "button-editor") return;
  // Design luminaires use their centre so the connection remains clearly attributable.
  if (element.closest('[data-connection-anchor="center"]')) return;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;
  if (Math.abs(point.clientX - (rect.left + rect.right) / 2) > 0.5
    || Math.abs(point.clientY - (rect.top + rect.bottom) / 2) > 0.5) return;
  // Editor keys always connect at their screen-space left/right edges, even when rotated.
  if (surface === "button-editor") return { type: "rectangle", width: rect.width, height: rect.height };
  if (typeof SVGGraphicsElement !== "undefined" && element instanceof SVGGraphicsElement) {
    const matrix = element.getScreenCTM();
    if (matrix) {
      const scaleX = Math.hypot(matrix.a, matrix.b);
      const scaleY = Math.hypot(matrix.c, matrix.d);
      const perpendicular = Math.abs(matrix.a * matrix.c + matrix.b * matrix.d) <= scaleX * scaleY * 0.0001;
      const box = element.getBBox();
      if (element.localName === "circle" && perpendicular && Math.abs(scaleX - scaleY) < 0.0001) {
        return { type: "circle", radius: box.width * scaleX / 2 };
      }
      if (perpendicular && box.width > 0 && box.height > 0) {
        return { type: "rectangle", width: box.width * scaleX, height: box.height * scaleY,
          rotation: Math.atan2(matrix.b, matrix.a) * 180 / Math.PI };
      }
    }
  }
  // HTML controls and skewed shapes use a conservative viewport rectangle.
  return { type: "rectangle", width: rect.width, height: rect.height };
}

/** Chooses the contour's outward direction toward the partner unless the provider specifies one. */
function contourDirection(endpoint: ContouredEndpoint, partner: Point): Direction {
  // Choose the facing side without allowing a distant partner above/below to select a vertical edge.
  if (endpoint.surface === "button-editor") {
    return { x: partner.clientX < endpoint.point.clientX ? -1 : 1, y: 0 };
  }
  const direction = endpoint.exitDirection;
  if (direction && Number.isFinite(direction.x) && Number.isFinite(direction.y) && Math.hypot(direction.x, direction.y) > 0) return direction;
  const dx = partner.clientX - endpoint.point.clientX;
  const dy = partner.clientY - endpoint.point.clientY;
  const contour = endpoint.contour;
  if (contour?.type === "rectangle") {
    const angle = (contour.rotation ?? 0) * Math.PI / 180;
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const localX = dx * cos + dy * sin;
    const localY = -dx * sin + dy * cos;
    if (Math.abs(localX) / contour.width >= Math.abs(localY) / contour.height) {
      const side = localX < 0 ? -1 : 1;
      return { x: side * cos, y: side * sin };
    }
    const side = localY < 0 ? -1 : 1;
    return { x: -side * sin, y: side * cos };
  }
  return dx || dy ? { x: dx, y: dy } : { x: 1, y: 0 };
}

/** Insets editor connections 4px into their keys; other surfaces retain their contour gap. */
export function endpointAtContour<T extends ContouredEndpoint>(endpoint: T, partner: Point,
  gap = endpoint.surface === "button-editor" ? -4 : CONNECTION_CONTOUR_GAP): T {
  const contour = endpoint.contour;
  if (!contour) return endpoint;
  if (contour.type === "circle" ? !Number.isFinite(contour.radius) || contour.radius <= 0
    : !Number.isFinite(contour.width) || !Number.isFinite(contour.height) || contour.width <= 0 || contour.height <= 0
      || !Number.isFinite(contour.rotation ?? 0)) return endpoint;
  const direction = contourDirection(endpoint, partner);
  const length = Math.hypot(direction.x, direction.y);
  const x = direction.x / length, y = direction.y / length;
  let distance: number;
  if (contour.type === "circle") distance = contour.radius;
  else {
    const angle = (contour.rotation ?? 0) * Math.PI / 180;
    const localX = x * Math.cos(angle) + y * Math.sin(angle);
    const localY = -x * Math.sin(angle) + y * Math.cos(angle);
    distance = Math.min(contour.width / 2 / Math.abs(localX), contour.height / 2 / Math.abs(localY));
  }
  return { ...endpoint, point: { clientX: endpoint.point.clientX + x * (distance + gap),
    clientY: endpoint.point.clientY + y * (distance + gap) }, exitDirection: { x, y } };
}

/** Includes shape changes in endpoint invalidation even when the centre does not move. */
export function endpointContoursEqual(a?: EndpointContour, b?: EndpointContour): boolean {
  if (!a || !b) return a === b;
  if (a.type === "circle" && b.type === "circle") return a.radius === b.radius;
  if (a.type === "rectangle" && b.type === "rectangle") return a.width === b.width && a.height === b.height && (a.rotation ?? 0) === (b.rotation ?? 0);
  return false;
}
